/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description EB segment mapping
 */

import _ from 'lodash';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';

// https://www.stedi.com/edi/x12-005010/segment/EB
const BENEFIT_TYPES = {
	1: {
		code: '1',
		display: 'Active Coverage',
	},
	2: {
		code: '2',
		display: 'Active - Full Risk Capitation',
	},
	3: {
		code: '3',
		display: 'Active - Services Capitated',
	},
	4: {
		code: '4',
		display: 'Active - Services Capitated to Primary Care Physician',
	},
	5: {
		code: '5',
		display: 'Active - Pending Investigation',
	},
	6: {
		code: '6',
		display: 'Inactive',
	},
	7: {
		code: '7',
		display: 'Inactive - Pending Eligibility Update',
	},
	8: {
		code: '8',
		display: 'Inactive - Pending Investigation',
	},
	A: {
		code: 'A',
		display: 'Co-Insurance',
	},
	AA: {
		code: 'AA',
		display: 'Patient Reimbursement',
	},
	AB: {
		code: 'AB',
		display: 'Co-payment Maximum',
	},
	AC: {
		code: 'AC',
		display: 'Co-insurance Maximum',
	},
	B: {
		code: 'B',
		display: 'Co-Payment',
	},
	C: {
		code: 'C',
		display: 'Deductible',
	},
	CB: {
		code: 'CB',
		display: 'Coverage Basis',
	},
	D: {
		code: 'D',
		display: 'Benefit Description',
	},
	E: {
		code: 'E',
		display: 'Exclusions',
	},
	F: {
		code: 'F',
		display: 'Limitations',
	},
	G: {
		code: 'G',
		display: 'Out of Pocket (Stop Loss)',
	},
	H: {
		code: 'H',
		display: 'Unlimited',
	},
	I: {
		code: 'I',
		display: 'Non-Covered',
	},
	J: {
		code: 'J',
		display: 'Cost Containment',
	},
	K: {
		code: 'K',
		display: 'Reserve',
	},
	L: {
		code: 'L',
		display: 'Primary Care Provider',
	},
	M: {
		code: 'M',
		display: 'Pre-existing Condition',
	},
	MC: {
		code: 'MC',
		display: 'Managed Care Coordinator',
	},
	N: {
		code: 'N',
		display: 'Services Restricted to Following Provider',
	},
	O: {
		code: 'O',
		display: 'Not Deemed a Medical Necessity',
	},
	P: {
		code: 'P',
		display: 'Benefit Disclaimer',
	},
	Q: {
		code: 'Q',
		display: 'Second Surgical Opinion Required',
	},
	R: {
		code: 'R',
		display: 'Other or Additional Payor',
	},
	S: {
		code: 'S',
		display: 'Prior Year(s) History',
	},
	T: {
		code: 'T',
		display: 'Card(s) Reported Lost/Stolen',
	},
	U: {
		code: 'U',
		display: 'Contact Following Entity for Eligibility or Benefit Information',
	},
	V: {
		code: 'V',
		display: 'Cannot Process',
	},
	W: {
		code: 'W',
		display: 'Other Source of Data',
	},
	WV: {
		code: 'WV',
		display: 'Waiver',
	},
	X: {
		code: 'X',
		display: 'Health Care Facility',
	},
	Y: {
		code: 'Y',
		display: 'Spend Down',
	},
	ZZ: {
		code: 'ZZ',
		display: 'Place of service',
	},
};

const SERVICE_STATUS = [
	BENEFIT_TYPES[1],
	BENEFIT_TYPES[2],
	BENEFIT_TYPES[3],
	BENEFIT_TYPES[4],
	BENEFIT_TYPES[5],
	BENEFIT_TYPES[6],
	BENEFIT_TYPES[7],
	BENEFIT_TYPES[8],
];
const ELIGIBILITY_ACTIVE_STATUS_CODES: EligibilityRequests.ICoverageStatus[] = ['1', '2', '3', '4', '5'];

const ELIGIBILITY_INACTIVE_STATUS_CODES: EligibilityRequests.ICoverageStatus[] = ['6', '7', '8'];

const ELIGIBILITY_BENEFIT_STATUS_CODES = _.keys(
	_.omit(_.cloneDeep(BENEFIT_TYPES), [1, 2, 3, 4, 5, 6, 7, 8]),
) as EligibilityRequests.ICoverageStatus[];

const PRIMARY_CARE_PROVIDER = {
	...BENEFIT_TYPES.L,
	url: 'AdditionalResourceReference',
};

const HEALTHCARE_CONTACT = {
	...BENEFIT_TYPES.U,
};

const OTHER_DATA_SOURCE = {
	...BENEFIT_TYPES.W,
};

const ADDITIONAL_PAYER = {
	...BENEFIT_TYPES.R,
	url: 'AdditionalResourceReference',
};

const INSURANCE_TYPE = {
	url: '/EB04',
};

const AMOUNT_QUALIFIER = {
	url: '/EB06',
};

const MESSAGING = {
	url: 'https://314e.com/x12/VER005010/SEGMENT/MSG/MSG01',
};

const DATE_TIME = {
	url: '/DTP03/291',
};

const BENEFIT_DESCRIPTION = {
	...BENEFIT_TYPES.D,
};

const BENEFIT_DISCLAIMER = {
	...BENEFIT_TYPES.P,
};

const ERROR_MESSAGE = {
	...BENEFIT_TYPES.V,
};

const PLACE_OF_SERVICE = {
	...BENEFIT_TYPES.ZZ,
};

const PARSED_BENEFIT_TYPES = [
	BENEFIT_TYPES.A,
	BENEFIT_TYPES.B,
	BENEFIT_TYPES.C,
	BENEFIT_TYPES.CB,
	BENEFIT_TYPES.D,
	BENEFIT_TYPES.E,
	BENEFIT_TYPES.F,
	BENEFIT_TYPES.G,
	BENEFIT_TYPES.H,
	BENEFIT_TYPES.I,
	BENEFIT_TYPES.J,
	BENEFIT_TYPES.K,
	BENEFIT_TYPES.L,
	BENEFIT_TYPES.M,
	BENEFIT_TYPES.MC,
	BENEFIT_TYPES.N,
	BENEFIT_TYPES.O,
	BENEFIT_TYPES.P,
	BENEFIT_TYPES.Q,
	BENEFIT_TYPES.R,
	BENEFIT_TYPES.S,
	BENEFIT_TYPES.T,
	BENEFIT_TYPES.U,
	BENEFIT_TYPES.V,
	BENEFIT_TYPES.W,
	BENEFIT_TYPES.X,
	BENEFIT_TYPES.Y,
];

const SERVICE_STATUS_CODES = {
	1: {
		label: 'Active Coverage',
		color: ANT_TAG_COLORS.success,
	},
	2: {
		label: 'Active - Full Risk Capitation',
		color: ANT_TAG_COLORS.success,
	},
	3: {
		label: 'Active - Services Capitated',
		color: ANT_TAG_COLORS.success,
	},
	4: {
		label: 'Active - Services Capitated to Primary Care Physician',
		color: ANT_TAG_COLORS.success,
	},
	5: {
		label: 'Active - Pending Investigation',
		color: ANT_TAG_COLORS.success,
	},
	6: {
		label: 'Inactive',
		color: ANT_TAG_COLORS.error,
	},
	7: {
		label: 'Inactive - Pending Eligibility Update',
		color: ANT_TAG_COLORS.error,
	},
	8: {
		label: 'Inactive - Pending Investigation',
		color: ANT_TAG_COLORS.error,
	},
};
const DTP_DATES = {
	'096': 'Discharge Date',
	102: 'Issue Date',
	152: 'Effective Change Date',
	193: 'Period Start Date',
	194: 'Period End Date',
	198: 'Completion Date',
	290: 'Coordination of Benefits Date',
	291: 'Plan Date',
	292: 'Benefit Date',
	295: 'Primary Care Provider Date',
	304: 'Latest Consultation Date',
	307: 'Eligibility Date',
	318: 'Added Date',
	340: 'Cobra Begin Date',
	341: 'Cobra End Date',
	342: 'Premium Paid to Begin Date',
	343: 'Premium Paid to End Date',
	346: 'Plan Begin Date',
	347: 'Plan End Date',
	348: 'Benefit Begin Date',
	349: 'Benefit End Date',
	356: 'Eligibility Begin Date',
	357: 'Eligibility End Date',
	382: 'Enrollment Date',
	435: 'Admission Date',
	442: 'Date of Death',
	458: 'Certification Date',
	472: 'Service Date',
	539: 'Policy Effective Date',
	540: 'Policy Expiration Date',
	636: 'Last Update Date',
	771: 'Status Date',
};

const ENTITY_IDENTIFIERS = {
	PI: 'payerID',
	MI: 'memberID',
	NI: 'naicID',
	46: 'etin',
	XX: 'npiNumber',
};

const TIME_QUALIFIER_MAPPING = {
	6: { code: 6, display: 'Hour' },
	7: { code: 7, display: 'Day' },
	13: { code: 13, display: '24 Hours' },
	21: { code: 21, display: 'Years' },
	22: { code: 22, display: 'Service Year' },
	23: { code: 23, display: 'Calendar Year' },
	24: { code: 24, display: 'Year to Date' },
	25: { code: 25, display: 'Contract' },
	26: { code: 26, display: 'Episode' },
	27: { code: 27, display: 'Visit' },
	28: { code: 28, display: 'Outlier' },
	29: { code: 29, display: 'Remaining' },
	30: { code: 30, display: 'Exceeded' },
	31: { code: 31, display: 'Not Exceeded' },
	32: { code: 32, display: 'Lifetime' },
	33: { code: 33, display: 'Lifetime Remaining' },
	34: { code: 34, display: 'Month' },
	35: { code: 35, display: 'Week' },
	36: { code: 36, display: 'Admission' },
};

const QUANTITY_QUALIFIER_MAPPING = {
	'8H': { code: '8H', display: 'Minimum' },
	99: { code: 99, display: 'Quantity Used' },
	CA: { code: 'CA', display: 'Covered - Actual' },
	CE: { code: 'CE', display: 'Covered - Estimated' },
	D3: { code: 'D3', display: 'Number of Co-insurance Days' },
	DB: { code: 'DB', display: 'Deductible Blood Units' },
	DY: { code: 'DY', display: 'Days' },
	HS: { code: 'HS', display: 'Hours' },
	LA: { code: 'LA', display: 'Life-time Reserve - Actual' },
	LE: { code: 'LE', display: 'Life-time Reserve - Estimated' },
	M2: { code: 'M2', display: 'Maximum' },
	MN: { code: 'MN', display: 'Month' },
	P6: { code: 'P6', display: 'Number of Services or Procedures' },
	QA: { code: 'QA', display: 'Quantity Approved' },
	S7: { code: 'S7', display: 'Age, High Value' },
	S8: { code: 'S8', display: 'Age, Low Value' },
	VS: { code: 'VS', display: 'Visits' },
	YY: { code: 'YY', display: 'Years' },
};

export {
	BENEFIT_TYPES,
	SERVICE_STATUS,
	ELIGIBILITY_BENEFIT_STATUS_CODES,
	PRIMARY_CARE_PROVIDER,
	INSURANCE_TYPE,
	ADDITIONAL_PAYER,
	AMOUNT_QUALIFIER,
	PARSED_BENEFIT_TYPES,
	BENEFIT_DESCRIPTION,
	BENEFIT_DISCLAIMER,
	MESSAGING,
	ERROR_MESSAGE,
	DATE_TIME,
	SERVICE_STATUS_CODES,
	PLACE_OF_SERVICE,
	DTP_DATES,
	ENTITY_IDENTIFIERS,
	HEALTHCARE_CONTACT,
	OTHER_DATA_SOURCE,
	TIME_QUALIFIER_MAPPING,
	QUANTITY_QUALIFIER_MAPPING,
	ELIGIBILITY_ACTIVE_STATUS_CODES,
	ELIGIBILITY_INACTIVE_STATUS_CODES,
};
